import { TaskType } from '@/shared/domain/taskTypes';
import { LABEL_PRIORITY_KIND } from '@/shared/domain/labelPriority';
import { labelPriorityAppliesTo } from '@/features/tasks/components/CreationWizard/businessGoals/labelPriority';
import { childRect, inputTextRect, unionRects } from '../utils/anchorRects';

/**
 * The coach-mark steps of the "Create a model" wizard, one factory per wizard
 * page (Figma MVP-DESIGNES 9410-10883 … 9461-16588). The factories return
 * fresh step objects so a walkthrough built from them (today only the demo
 * tour, `createModelWalkthrough.js`) can adjust copy or predicates without
 * touching the factory's own.
 *
 * Step shape:
 * - `id`               stable key, persisted as `step_id` so a reload resumes here
 * - `wizardStep`       wizard page (`?step=`) the coach mark belongs to
 * - `target`           `data-walkthrough` attribute value of the anchored element
 *                      (`selector` overrides it with a raw CSS selector when the
 *                      anchor lives inside a component that cannot carry the attr)
 * - `placement`/`align` where the bubble sits relative to the target
 *                      (`top|bottom|left|right`, `start|center|end`)
 * - `anchorRect(el, rect)` optional: replaces the element's bounding box as the
 *                      highlighted / pointed-at area (see `utils/anchorRects.js`)
 * - `observe`          optional selectors of document-level elements (portaled
 *                      panels) whose mount/move/unmount re-measures `anchorRect`
 * - `isApplicable(ctx)` false ⇒ the step is passed over silently and excluded
 *                      from "Step N/Total" (task-type / autonomy / provider
 *                      gating); it comes back if it applies again while the
 *                      user is still on its page
 * - `isComplete(ctx)`  true ⇒ the action the bubble asks for is done; the
 *                      walkthrough advances (also when already satisfied)
 * - `completeOnEvents` DOM events on the target that count as completion for
 *                      click-in-place actions with no observable state
 * - `hint`             optional `{ selector, offset? }`: a second, dashed outline
 *                      around the one option / card the bubble asks the user to
 *                      pick (Figma 9455-7106). The selector is resolved against
 *                      the document (the option usually lives in a portaled
 *                      panel); `offset` inflates the box (default 4px)
 * - `activation`       how the demo tour reacts to the user acting on the anchor:
 *                      default — any click / key press on the anchor is turned
 *                      into the step's auto-fill; `'option'` — only a click on
 *                      the hinted element counts (the host's own handler runs,
 *                      then the step completes), everything else is swallowed;
 *                      `'passthrough'` — no interception at all (native drag &
 *                      drop), completion comes from `isComplete`
 *
 * `ctx` is whatever the wizard publishes through `useWalkthroughContextValue`
 * / `publishWalkthroughContext`: `{ wizardPages, form, dataset, modelSetup,
 * dataEditor, train }`. Predicates must tolerate missing slices (page not
 * mounted); the slices listed in the definition's `requiredContext` are the
 * exception — the runner waits for them before it evaluates anything.
 *
 * A step's anchor must exist by the time the pointer reaches it, otherwise the
 * tour shows nothing until it does. Where the wizard reveals a section only
 * after another choice (labels after the task type, single/multi for
 * classification), the choice gets its own step so the user is never left
 * without a mark.
 */

/** Wizard pages that carry coach marks, in wizard order (Agent Config has none). */
export const WIZARD_WALKTHROUGH_PAGES = Object.freeze([
    'business-goals',
    'data-collection',
    'model-setup',
    'data-editor',
    'train',
]);

const text = (value) => (typeof value === 'string' ? value.trim() : '');

const LABELLED_TASK_TYPES = new Set([TaskType.classification, TaskType.NER]);

/** Labels/entities sections exist for classification and NER; unknown type ⇒ wait. */
export const labelsApply = (ctx) => {
    const type = ctx.form?.taskType;
    return type == null || LABELLED_TASK_TYPES.has(type);
};

export const isClassification = (ctx) => {
    const type = ctx.form?.taskType;
    return type == null || type === TaskType.classification;
};

/**
 * "What matters most for these labels?" replaces the Fallback select for
 * single-label classification with exactly two labels — mirror
 * `BusinessGoalsStep.showLabelPrioritySection`. Both steps are deferred rather
 * than skipped while they do not apply, so adding or removing a label while
 * still on the page brings the right mark back instead of moving on.
 */
export const labelPriorityApplies = (ctx) => {
    const form = ctx.form || {};
    return (
        isClassification(ctx) &&
        labelPriorityAppliesTo({ classificationType: form.classificationType, labels: form.labels })
    );
};

/**
 * The Fallback select is shown unless the label-priority question replaces
 * it, or again once the user answers "equally important" — mirror
 * `BusinessGoalsStep.showFallbackSection` so the step is not left waiting for
 * a select that never renders.
 */
export const fallbackApplies = (ctx) => {
    if (!isClassification(ctx)) return false;
    if (!labelPriorityApplies(ctx)) return true;
    return ctx.form?.labelPriority?.kind === LABEL_PRIORITY_KIND.equal;
};

/** True when `a` and `b` sit next to each other in `order` (either way round). */
export const isAdjacent = (order, a, b) => {
    if (!Array.isArray(order)) return false;
    const ia = order.indexOf(a);
    const ib = order.indexOf(b);
    return ia >= 0 && ib >= 0 && Math.abs(ia - ib) === 1;
};

/**
 * Marker class a host puts on the one option / row a step's dashed `hint`
 * points at (the "neutral" fallback label, the "LLM created" trust level,
 * the gpt-5 model, the "negative" label). The engine draws the outline from
 * the element's box; the class itself has no styling.
 */
export const WALKTHROUGH_HINT_ITEM_CLASS = 'walkthrough-hint-item';

/** "Data was created by" select trigger inside its anchor, and its options panel (`DatasetDetailsForm.DataCreationSelect`). */
export const DATA_CREATION_TRIGGER_SELECTOR = '.spark-dropdown-descriptive-menu';
export const DATA_CREATION_PANEL_SELECTOR = '.cw-dc-data-creation-dropdown-panel';

/** "Fallback label" select trigger inside its anchor, and its options panel (`FallbackLabelSelect`). */
export const FALLBACK_LABEL_TRIGGER_SELECTOR = '.cw-fallback-label-section__select';
export const FALLBACK_LABEL_PANEL_SELECTOR = '.cw-label-options-panel';

/** "Select commercial model" trigger inside its anchor, and its options panel (`ModelSelection`). */
export const MODEL_SELECT_TRIGGER_SELECTOR = '.model-selection__trigger';
export const MODEL_SELECT_PANEL_SELECTOR = '.model-selection__panel';

/**
 * Row-label select trigger inside its anchor, and the antd options panel it
 * opens (`LabelSelector` tags its popup with `label-selector__popup`, so
 * another open antd select on the page is never mistaken for it).
 */
export const ROW_LABEL_TRIGGER_SELECTOR = '.ant-select';
export const ROW_LABEL_PANEL_SELECTOR = '.label-selector__popup:not(.ant-select-dropdown-hidden)';

const hintIn = (panelSelector, offset) => ({
    selector: `${panelSelector} .${WALKTHROUGH_HINT_ITEM_CLASS}`,
    ...(offset != null ? { offset } : {}),
});

// ── Business goals ─────────────────────────────────────────────────────
export const businessGoalsSteps = () => [
    {
        id: 'task-name',
        wizardStep: 'business-goals',
        target: 'task-name',
        title: 'Keep your tasks organized',
        body: "Click here to give your task a clear name so it's easy to find and manage later.",
        placement: 'bottom',
        // Outline the words, not the full-width row (Figma 9410-10883).
        anchorRect: inputTextRect,
        // Done when the name is committed (blur / Enter), not on the first
        // keystroke — the mark stays put while the user is still typing.
        completeOnEvents: ['change'],
    },
    {
        id: 'task-type',
        wizardStep: 'business-goals',
        target: 'task-type',
        title: 'What should the model do?',
        body: 'Pick the task type. It decides what the model returns — labels, extracted entities or generated text — and which of the next steps apply.',
        placement: 'bottom',
        align: 'start',
        isComplete: (ctx) => Boolean(ctx.form?.taskType),
    },
    {
        id: 'classification-type',
        wizardStep: 'business-goals',
        target: 'classification-type',
        title: 'One label or several?',
        body: 'Tell us whether a single example can carry more than one label. It shapes how labels are predicted and reviewed.',
        placement: 'bottom',
        align: 'start',
        isApplicable: isClassification,
        isComplete: (ctx) => Boolean(ctx.form?.classificationType),
    },
    {
        id: 'labels',
        wizardStep: 'business-goals',
        target: 'labels',
        title: 'Match your data labels',
        body: 'Click to add the exact labels used in your files. This helps us align with your naming structure and prevents mapping errors.',
        placement: 'right',
        isApplicable: labelsApply,
        isComplete: (ctx) => (ctx.form?.labels || []).length > 0,
    },
    {
        id: 'label-priority',
        wizardStep: 'business-goals',
        target: 'label-priority',
        title: 'Which label matters more?',
        body: 'With two labels, tell us what matters most. Your answer sets the fallback label and the training defaults; keep them equally important to pick the fallback yourself.',
        placement: 'right',
        isApplicable: labelPriorityApplies,
        isComplete: (ctx) => Boolean(ctx.form?.labelPriority?.kind),
    },
    {
        id: 'fallback-label',
        wizardStep: 'business-goals',
        target: 'fallback-label',
        title: 'Your safety net',
        body: 'Click to choose a default label to apply when prediction confidence is low.',
        placement: 'right',
        // One outline around the select and its open options (Figma
        // 9430-3521); the dashed hint sits on the "neutral" option.
        anchorRect: unionRects(FALLBACK_LABEL_TRIGGER_SELECTOR, FALLBACK_LABEL_PANEL_SELECTOR),
        observe: [FALLBACK_LABEL_PANEL_SELECTOR],
        hint: hintIn(FALLBACK_LABEL_PANEL_SELECTOR),
        activation: 'option',
        isApplicable: fallbackApplies,
        isComplete: (ctx) => ctx.form?.fallbackLabel != null && ctx.form.fallbackLabel !== '',
    },
    {
        id: 'priority-ranking',
        wizardStep: 'business-goals',
        target: 'priority-ranking',
        title: 'What matters most to you?',
        body: "Drag and drop to tell us what's important. We'll tailor the platform to match your trade-offs. We use this to balance cost, speed, and quality for your specific needs.",
        placement: 'top',
        // Figma 9430-3293: solid outline on the card to drag ("Cost"), dashed
        // hint on where to drop it ("Quality"); done once the two touch.
        anchorRect: childRect('[data-pillar="cost"]'),
        hint: { selector: '[data-walkthrough="priority-ranking"] [data-pillar="quality"]', offset: 16 },
        activation: 'passthrough',
        isComplete: (ctx) => isAdjacent(ctx.form?.priorityOrder, 'cost', 'quality'),
    },
];

// ── Data collection ────────────────────────────────────────────────────
export const dataCollectionSteps = () => [
    // Hints on the header buttons: they only tell the user the option exists,
    // so they complete on the click alone and show even once files are in.
    {
        id: 'no-data',
        wizardStep: 'data-collection',
        target: 'need-dataset',
        title: 'No data? No problem',
        body: 'You can use our AI to generate synthetic data, run a retrieval agent to pull what you need, or import an existing set from Hugging Face.',
        placement: 'left',
        completeOnEvents: ['click'],
    },
    {
        id: 'template',
        wizardStep: 'data-collection',
        target: 'download-template',
        title: 'Or - Start with a template',
        body: 'Download a pre-structured template in JSON, JSONL, or CSV to ensure your file is formatted correctly and uploads perfectly on the first try.',
        placement: 'right',
        completeOnEvents: ['click'],
    },
    {
        id: 'upload-data',
        wizardStep: 'data-collection',
        target: 'upload-cards',
        title: 'Add your data',
        body: 'Upload the train dataset and benchmark files that represent the task you want your model to learn from and asses by',
        placement: 'bottom',
        isComplete: (ctx) => ctx.dataset?.hasFiles === true,
    },
    {
        id: 'data-creation',
        wizardStep: 'data-collection',
        target: 'data-creation',
        title: 'Verify data trust level',
        body: "Tell us how this data was generated. Knowing whether it's human-verified or AI-generated helps us understand how to treat your data validity",
        placement: 'right',
        // One outline around the select and its open options, label left
        // out (Figma 9455:7106). The options panel is portaled to the body.
        anchorRect: unionRects(DATA_CREATION_TRIGGER_SELECTOR, DATA_CREATION_PANEL_SELECTOR),
        observe: [DATA_CREATION_PANEL_SELECTOR],
        hint: hintIn(DATA_CREATION_PANEL_SELECTOR),
        activation: 'option',
        isComplete: (ctx) => Boolean(ctx.dataset?.dataCreation),
    },
    {
        id: 'example-preview',
        wizardStep: 'data-collection',
        target: 'shuffle-example',
        title: 'Preview your data',
        body: 'Shuffle through examples to confirm we are reading the right information from your file.',
        placement: 'top',
        completeOnEvents: ['click'],
    },
    {
        id: 'input-field',
        wizardStep: 'data-collection',
        target: 'input-field',
        title: 'Map your input data',
        body: 'Select the column or JSON key from your file that holds the raw text. This tells the model exactly what it needs to read and classify.',
        placement: 'top',
        isComplete: (ctx) => Boolean(ctx.dataset?.exampleColumn),
    },
    {
        id: 'label-mapping',
        wizardStep: 'data-collection',
        target: 'label-mapping',
        title: 'Map unmatched labels',
        body: 'Sometimes your file data uses different names. Use this dropdown to link the unrecognized labels directly to your exact goals.',
        placement: 'top',
        align: 'start',
        isApplicable: labelsApply,
        // Defined once labels were extracted; 0 ⇒ every file label maps to a
        // task label (either from the start or after the user re-mapped them).
        isComplete: (ctx) => ctx.dataset?.unmatchedLabelsCount === 0,
    },
    {
        // Only while the "Missing Label as defined in task creation" banner is
        // up: the data-collection panel reports it, the Fix menu opens itself.
        id: 'missing-labels',
        wizardStep: 'data-collection',
        target: 'missing-labels',
        title: 'Some labels are missing from your data',
        body: 'Your task defines labels that never appear in the uploaded files. Use Fix to add them to the task, generate new data for them, or add more files.',
        placement: 'bottom',
        align: 'start',
        isApplicable: (ctx) => ctx.dataset?.missingLabelsShown === true,
        // Applicability is checked first, so a banner that goes away defers
        // the step (it returns if a banner shows again on this page); the
        // predicate only states that "no banner" is the finished state.
        isComplete: (ctx) => ctx.dataset?.missingLabelsShown !== true,
    },
];

// ── Model setup ────────────────────────────────────────────────────────
export const modelSetupSteps = () => [
    {
        id: 'commercial-model',
        wizardStep: 'model-setup',
        target: 'model-select',
        title: 'What are you using today?',
        body: 'Select the model you want to compare our results against so you can measure the difference in performance.',
        placement: 'right',
        // One outline around the trigger and its open list (Figma 9455-9766);
        // the dashed hint sits on the gpt-5 row. The list is portaled.
        anchorRect: unionRects(MODEL_SELECT_TRIGGER_SELECTOR, MODEL_SELECT_PANEL_SELECTOR),
        observe: [MODEL_SELECT_PANEL_SELECTOR],
        hint: hintIn(MODEL_SELECT_PANEL_SELECTOR),
        activation: 'option',
        isComplete: (ctx) => Boolean(ctx.modelSetup?.teacherModel),
    },
    {
        id: 'system-prompt',
        wizardStep: 'model-setup',
        target: 'system-prompt',
        title: 'Add system prompt',
        body: 'Click here to give the model its core instructions and available labels. The specific text you want it to analyze goes next in the User Prompt.',
        placement: 'top',
        isComplete: (ctx) => text(ctx.modelSetup?.systemPrompt).length > 0,
    },
    {
        id: 'user-prompt',
        wizardStep: 'model-setup',
        target: 'user-prompt',
        title: 'Give the specific insturction',
        body: 'Click to add extraction command and inject the specific data fields you want to incorporate',
        placement: 'right',
        isComplete: (ctx) => text(ctx.modelSetup?.userPrompt).length > 0,
    },
    {
        id: 'output-format',
        wizardStep: 'model-setup',
        target: 'output-format',
        title: 'Structure the results',
        body: 'If needed, define the exact JSON schema you need. This forces the model to return data in a predictable format that your system can easily parse.',
        placement: 'right',
        isApplicable: (ctx) => ctx.modelSetup?.supportsJsonSchema !== false,
        // The tour switches the section on itself, so only a schema the user
        // actually wrote (or inferred) counts as done.
        isComplete: (ctx) => text(ctx.modelSetup?.schema).length > 0,
    },
];

// ── Data editor ────────────────────────────────────────────────────────
export const dataEditorSteps = () => [
    {
        id: 'label-view',
        wizardStep: 'data-editor',
        // The "View" button lives inside Spark's ValidationCard, which only
        // accepts a className — hence a raw selector instead of `target`.
        selector: '.walkthrough-label-card .spark-validation-card-header-with-review .spark-btn',
        title: 'Focus on one label',
        body: "Click 'View' to isolate this specific label in the table below. This hides the rest of the data so you can review one category at a time without distractions.",
        placement: 'top',
        isApplicable: labelsApply,
        completeOnEvents: ['click'],
    },
    {
        id: 'row-label',
        wizardStep: 'data-editor',
        target: 'row-label',
        title: 'Review and correct',
        body: 'You have the final say. If the system categorized a task incorrectly, use this dropdown to switch it to the right label.',
        // Beside the cell, not under it: the label dropdown opens downwards
        // and a bubble below would sit on top of the options.
        placement: 'left',
        // One outline around the select and its open options; the dashed
        // hint sits on the "negative" option. The antd panel is portaled.
        anchorRect: unionRects(ROW_LABEL_TRIGGER_SELECTOR, ROW_LABEL_PANEL_SELECTOR),
        observe: [ROW_LABEL_PANEL_SELECTOR],
        hint: hintIn(ROW_LABEL_PANEL_SELECTOR),
        activation: 'option',
        isApplicable: isClassification,
        isComplete: (ctx) => ctx.dataEditor?.labelChanged === true,
    },
    {
        id: 'reviewed',
        wizardStep: 'data-editor',
        target: 'reviewed-checkbox',
        title: 'Approve to improve',
        body: 'Check this box to confirm the example is correct. Once you approve 30 examples, the model has enough data to reach optimal accuracy.',
        placement: 'bottom',
        // The cell wrapper stretches to the row height; Figma 9461-15277 outlines only the checkbox + label.
        anchorRect: childRect('.spark-checkbox'),
        completeOnEvents: ['change'],
    },
];

// ── Train ──────────────────────────────────────────────────────────────
export const trainSteps = () => [
    {
        // The Train page opens the Advanced accordion while this step is up
        // (see finetuning/Advanced.jsx); touching any option inside is what
        // completes it and keeps the accordion open.
        id: 'advanced-settings',
        wizardStep: 'train',
        target: 'advanced-settings',
        title: 'Adjust advanced settings',
        body: 'Create your specialized model. Review your task configuration, prompt, and chosen datasets. You can adjust Training Device (CPU/GPU) and the specific metric to optimize (what is most important to you).',
        placement: 'top',
        isComplete: (ctx) => ctx.train?.advancedTouched === true,
    },
];

/** Every wizard step, page by page, in the order the user meets them. */
export const allWizardSteps = () => [
    ...businessGoalsSteps(),
    ...dataCollectionSteps(),
    ...modelSetupSteps(),
    ...dataEditorSteps(),
    ...trainSteps(),
];

/** The steps of `factory()` whose ids are listed, in list order; unknown ids throw at module load. */
export const pickSteps = (factory, ids) => {
    const byId = new Map(factory().map((step) => [step.id, step]));
    return ids.map((id) => {
        const step = byId.get(id);
        if (!step) throw new Error(`Unknown wizard walkthrough step: ${id}`);
        return step;
    });
};
